import { useEffect, useRef, useState } from "react";
import { updateStateObjectData } from "../../../utility/Common";
import RapHousieEntertainmentEntityDialog from "./RapHousieEntertainmentEntityDialog";
import { toast } from "react-toastify";
import { generatePublicUrlForFileInAWSS3Bucket } from "../../../services/uploadFileOnAWS_S3_Bucket";
import {
    extractCloudflareStreamId,
    getCloudflareStreamEmbedUrl,
    resolveVideoPreview,
} from "../../../network/cloudflare_stream_service/CloudflareStream_api_callback";

/** Timeline times/duration from API are usually ms; player APIs need seconds. */
const toSeekSeconds = (time, durationValue) => {
    const t = Number(time);
    const d = Number(durationValue);
    if (!Number.isFinite(t) || t < 0) return 0;

    const durationLooksMs = Number.isFinite(d) && d > 10000;
    if (durationLooksMs) {
        // duration in ms (e.g. 600000). Times may be ms (1000, 11000) or seconds (1, 11).
        // Small values relative to an ms-duration are treated as seconds.
        if (t >= 1000 || t > d / 50) return t / 1000;
        return t;
    }

    // duration in seconds (or unknown): if time is clearly ms, convert
    if (Number.isFinite(d) && t > d && t >= 1000) return t / 1000;
    if (!Number.isFinite(d) && t >= 1000) return t / 1000;
    return t;
};

const buildCloudflareEmbedSrc = (videoId, startSeconds = 0, autoplay = false) => {
    if (!videoId) return "";
    const params = new URLSearchParams();
    params.set("preload", "auto");
    params.set("controls", "true");
    params.set("autoplay", autoplay ? "true" : "false");
    console.log("data-play-video",autoplay);
    // Always play with audio — never start muted
    params.set("muted", "false");
    const start = Math.max(0, Number(startSeconds) || 0);
    // Cloudflare accepts plain seconds: startTime=11
    if (start > 0) {
        params.set("startTime", String(Math.floor(start)));
    }
    // Bust cache so remount always applies new startTime
    if (autoplay || start > 0) {
        params.set("_t", String(Date.now()));
    }
    return `${getCloudflareStreamEmbedUrl(videoId)}?${params.toString()}`;
};

const VideoPlayerWithMarker = ({ multimediaDetail, setMultimediaDetail }) => {
    const videoRef = useRef(null);
    const iframeRef = useRef(null);
    const streamPlayerRef = useRef(null);
    const markersRef = useRef([]);
    const durationRef = useRef(0);
    const selectedMarkerRef = useRef(null);
    const cfTimeSyncBoundRef = useRef(null);

    const [videoSrc, setVideoSrc] = useState("");
    const [cloudflareVideoId, setCloudflareVideoId] = useState("");
    const [playerType, setPlayerType] = useState("vimeo"); // cloudflare | vimeo | direct
    const [iframeKey, setIframeKey] = useState(0);
    const [duration, setDuration] = useState(0);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [selectedMarkerIndex, setSelectedMarkerIndex] = useState(-1);

    const [markers, setMarkers] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editText, setEditText] = useState("");

    const [isOpenEntertainmentEntityDialog, setIsOpenEntertainmentEntityDialog] = useState(false);
    const [activeMarkerIndex, setActiveMarkerIndex] = useState(null);

    useEffect(() => {
        markersRef.current = markers;
    }, [markers]);

    useEffect(() => {
        durationRef.current = duration;
    }, [duration]);

    useEffect(() => {
        selectedMarkerRef.current = selectedMarker;
    }, [selectedMarker]);

    useEffect(() => {
        if (!multimediaDetail) {
            setVideoSrc("");
            setCloudflareVideoId("");
            streamPlayerRef.current = null;
            return;
        }

        const preview = resolveVideoPreview({
            form: "video",
            url: multimediaDetail.videoUrl,
            cloudFlareVideoId: multimediaDetail.cloudFlareVideoId,
        });

        const nextMarkers = multimediaDetail.timeLineV2 || [];
        const nextDuration = multimediaDetail.duration || 0;
        setMarkers(nextMarkers);
        setDuration(nextDuration);

        if (preview.type === "cloudflare" && preview.id) {
            setPlayerType("cloudflare");
            setCloudflareVideoId((prev) => {
                // Only reset embed when video id changes — never wipe an in-progress seek remount
                if (prev !== preview.id) {
                    setVideoSrc(buildCloudflareEmbedSrc(preview.id, 0, false));
                    setIframeKey((k) => k + 1);
                    streamPlayerRef.current = null;
                }
                return preview.id;
            });
            return;
        }

        if (
            multimediaDetail.playerType === "cloudflare" &&
            (multimediaDetail.cloudFlareVideoId || multimediaDetail.progressiveLink)
        ) {
            const cfId =
                multimediaDetail.cloudFlareVideoId ||
                extractCloudflareStreamId(multimediaDetail.progressiveLink) ||
                extractCloudflareStreamId(multimediaDetail.videoUrl);
            setPlayerType("cloudflare");
            setCloudflareVideoId((prev) => {
                if (cfId && prev !== cfId) {
                    setVideoSrc(buildCloudflareEmbedSrc(cfId, 0, false));
                    setIframeKey((k) => k + 1);
                    streamPlayerRef.current = null;
                } else if (!cfId && multimediaDetail.progressiveLink) {
                    setVideoSrc(multimediaDetail.progressiveLink);
                }
                return cfId || "";
            });
            return;
        }

        if (multimediaDetail.progressiveLink) {
            setPlayerType(multimediaDetail.playerType === "cloudflare" ? "cloudflare" : "vimeo");
            setCloudflareVideoId("");
            setVideoSrc(multimediaDetail.progressiveLink);
            return;
        }

        setPlayerType("direct");
        setCloudflareVideoId("");
        setVideoSrc("");
    }, [multimediaDetail]);

    // Load Cloudflare Stream SDK once for programmatic seek/play
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (window.Stream) return;
        const existing = document.querySelector('script[data-cf-stream-sdk="1"]');
        if (existing) return;
        const script = document.createElement("script");
        script.src = "https://embed.cloudflarestream.com/embed/sdk.latest.js";
        script.async = true;
        script.dataset.cfStreamSdk = "1";
        document.body.appendChild(script);
    }, []);

    const isCloudflarePlayer =
        playerType === "cloudflare" ||
        (typeof videoSrc === "string" &&
            (videoSrc.includes("videodelivery.net") || videoSrc.includes("cloudflarestream.com")));

    // Capture video duration once loaded
    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            const mediaDurationSec = videoRef.current.duration;
            if (Number.isFinite(mediaDurationSec) && mediaDurationSec > 0) {
                const existing = Number(duration);
                setDuration(existing > 10000 ? mediaDurationSec * 1000 : mediaDurationSec);
            }
        }
    };

    const ensureUnmuted = (playerOrVideo) => {
        if (!playerOrVideo) return;
        try {
            playerOrVideo.muted = false;
            if ("volume" in playerOrVideo && Number(playerOrVideo.volume) === 0) {
                playerOrVideo.volume = 1;
            }
        } catch (_) {
            /* ignore */
        }
    };

    const playWithAudio = (playerOrVideo) => {
        if (!playerOrVideo) return;
        ensureUnmuted(playerOrVideo);
        const playPromise = playerOrVideo.play?.();
        if (playPromise?.catch) {
            playPromise.catch(() => {
                // Never mute to recover playback — keep audio enabled
                ensureUnmuted(playerOrVideo);
            });
        }
        // Cloudflare may flip mute after seek/remount; keep correcting briefly
        const started = Date.now();
        const timer = window.setInterval(() => {
            ensureUnmuted(playerOrVideo);
            if (Date.now() - started > 2500) window.clearInterval(timer);
        }, 200);
    };

    /** Active marker = latest timeline entry whose time <= current playback (seconds). */
    const findMarkerIndexForTime = (currentTimeSec, markerList = markersRef.current, dur = durationRef.current) => {
        if (!Array.isArray(markerList) || markerList.length === 0) return -1;
        let activeIdx = -1;
        let bestT = -Infinity;
        for (let i = 0; i < markerList.length; i++) {
            const t = toSeekSeconds(markerList[i]?.time, dur);
            if (!Number.isFinite(t)) continue;
            if (t <= currentTimeSec + 0.35 && t >= bestT) {
                bestT = t;
                activeIdx = i;
            }
        }
        return activeIdx;
    };

    const syncSelectedMarkerFromTime = (currentTimeSec) => {
        if (!Number.isFinite(currentTimeSec)) return;
        const list = markersRef.current;
        const idx = findMarkerIndexForTime(currentTimeSec, list, durationRef.current);
        if (idx < 0) return;

        const next = list[idx];
        const prev = selectedMarkerRef.current;
        const same =
            prev &&
            String(prev.time) === String(next.time) &&
            String(prev.entityId || prev.entityID || "") === String(next.entityId || next.entityID || "") &&
            String(prev.multimediaId || "") === String(next.multimediaId || "");

        if (!same) {
            setSelectedMarker(next);
            setSelectedMarkerIndex(idx);
        } else {
            setSelectedMarkerIndex((prevIdx) => (prevIdx === idx ? prevIdx : idx));
        }
    };

    const selectMarker = (marker, index = -1) => {
        setSelectedMarker(marker);
        if (index >= 0) {
            setSelectedMarkerIndex(index);
        } else {
            const list = markersRef.current;
            const found = list.findIndex(
                (m) =>
                    String(m?.time) === String(marker?.time) &&
                    String(m?.entityId || m?.entityID || "") === String(marker?.entityId || marker?.entityID || "")
            );
            setSelectedMarkerIndex(found);
        }
    };

    const isMarkerSelected = (marker, index) => {
        if (selectedMarkerIndex >= 0) return selectedMarkerIndex === index;
        if (!selectedMarker || !marker) return false;
        return (
            String(selectedMarker.time) === String(marker.time) &&
            String(selectedMarker.entityId || selectedMarker.entityID || "") ===
                String(marker.entityId || marker.entityID || "")
        );
    };

    const attachCloudflareTimeSync = (player) => {
        if (!player || typeof player.addEventListener !== "function") return;
        if (cfTimeSyncBoundRef.current) {
            try {
                player.removeEventListener?.("timeupdate", cfTimeSyncBoundRef.current);
            } catch (_) {
                /* ignore */
            }
        }
        const onTimeUpdate = () => {
            const t = Number(player.currentTime);
            syncSelectedMarkerFromTime(t);
        };
        cfTimeSyncBoundRef.current = onTimeUpdate;
        player.addEventListener("timeupdate", onTimeUpdate);
        // Immediate sync for current position
        onTimeUpdate();
    };

    const seekCloudflareTo = async (seekSeconds) => {
        const start = Math.max(0, Number(seekSeconds) || 0);
        const videoId =
            cloudflareVideoId ||
            extractCloudflareStreamId(multimediaDetail?.cloudFlareVideoId) ||
            extractCloudflareStreamId(multimediaDetail?.videoUrl) ||
            extractCloudflareStreamId(videoSrc);

        if (!videoId) {
            console.error("Cloudflare seek failed: missing video id");
            toast.error("Cannot seek — Cloudflare video id missing");
            return false;
        }

        // Prefer in-place seek during click (user gesture → unmuted play works)
        const iframeNow = iframeRef.current || document.getElementById("raphousie-cloudflare-player");
        const StreamCtor = typeof window !== "undefined" ? window.Stream : null;
        if (iframeNow && StreamCtor) {
            try {
                const player = streamPlayerRef.current || StreamCtor(iframeNow);
                streamPlayerRef.current = player;
                if (Number(player.duration) > 0) {
                    player.currentTime = start;
                    playWithAudio(player);
                    attachCloudflareTimeSync(player);
                    syncSelectedMarkerFromTime(start);
                    // Still remount below so position is reliable; audio stays unmuted
                }
            } catch (_) {
                /* remount below */
            }
        }

        // Remount at startTime with muted=false (audio on)
        const nextSrc = buildCloudflareEmbedSrc(videoId, start, true);
        console.log("Cloudflare seek remount:", { videoId, start, nextSrc });
        setCloudflareVideoId(videoId);
        setVideoSrc(nextSrc);
        setIframeKey((k) => k + 1);
        streamPlayerRef.current = null;

        const applyAfterRemount = (attempt = 0) => {
            const iframe = iframeRef.current || document.getElementById("raphousie-cloudflare-player");
            const StreamApi = typeof window !== "undefined" ? window.Stream : null;
            if (!iframe || !StreamApi) {
                if (attempt < 12) window.setTimeout(() => applyAfterRemount(attempt + 1), 200);
                return;
            }
            try {
                const player = StreamApi(iframe);
                streamPlayerRef.current = player;
                player.currentTime = start;
                playWithAudio(player);
                attachCloudflareTimeSync(player);
                syncSelectedMarkerFromTime(start);
            } catch (err) {
                console.warn("Cloudflare SDK post-remount seek skipped:", err);
                if (attempt < 12) window.setTimeout(() => applyAfterRemount(attempt + 1), 200);
            }
        };
        window.setTimeout(() => applyAfterRemount(0), 250);

        return true;
    };

    // Seek + play video at marker time (Vimeo progressive or Cloudflare)
    const handleMarkerClick = async (marker, event, index = -1) => {
        // Don't seek when interacting with inputs/buttons inside the row
        if (event?.target?.closest?.("input, button, label, textarea, select")) return;
        if (!marker) return;

        const seekSeconds = toSeekSeconds(marker.time, duration);
        console.log("Seeking to marker time (s):", seekSeconds, {
            rawTime: marker.time,
            duration,
            entityName: marker.entityName,
        });
        selectMarker(marker, index);

        if (isCloudflarePlayer) {
            await seekCloudflareTo(seekSeconds);
            return;
        }

        const video = videoRef.current;
        if (!video) {
            toast.error("Video player not ready");
            return;
        }

        const applySeek = () => {
            try {
                video.currentTime = seekSeconds;
                playWithAudio(video);
                syncSelectedMarkerFromTime(seekSeconds);
            } catch (err) {
                console.error("Seek/play failed:", err);
            }
        };

        if (video.readyState >= 1) {
            applySeek();
        } else {
            video.addEventListener("loadedmetadata", applySeek, { once: true });
        }
    };

    // Add marker at current time (with +10s from last marker if exists)
    const addMarker = () => {
        if (!videoRef?.current && !isCloudflarePlayer && markers.length === 0) return;

        let newTime = 1;
        const unitIsMs = Number(duration) > 10000;

        if (markers.length > 0) {
            const lastTime = markers[markers.length - 1].time;
            const step = unitIsMs ? 10000 : 10;

            if (lastTime + step >= duration) {
                console.warn("⛔ Cannot add marker beyond video length");
                toast.error("Cannot add marker beyond video length");
                return;
            }

            newTime = lastTime + step;
        } else {
            newTime = unitIsMs ? 1000 : 1;
        }

        const newMarkers = [
            ...markers,
            {
                time: newTime,
                entityId: "",
                multimediaId: "",
            },
        ].sort((a, b) => a.time - b.time);

        setMarkers(newMarkers);
        updateStateObjectData(multimediaDetail, setMultimediaDetail, "timeLine", newMarkers);
        updateStateObjectData(multimediaDetail, setMultimediaDetail, "timeLineV2", newMarkers);
    };

    const formatTime = (time) => {
        const seconds = toSeekSeconds(time, duration);
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs.toString().padStart(2, "0")}`;
    };

    const saveEdit = (index) => {
        setMarkers((prev) => prev.map((marker, i) => (i === index ? { ...marker, text: editText } : marker)));
        setEditingIndex(null);
        setEditText("");
    };

    const handleChange = (value) => {
        updateStateObjectData(multimediaDetail, setMultimediaDetail, `timeLineV2.${activeMarkerIndex}`, value);
    };

    const handleTimeChange = (index, value) => {
        let newTime = Number(value);
        if (isNaN(newTime)) return;

        const prevTime = index > 0 ? markers[index - 1].time : 0;
        const nextTime = index < markers.length - 1 ? markers[index + 1].time : duration;
        newTime = Math.max(prevTime, Math.min(newTime, nextTime));

        const newMarkers = markers
            .map((marker, i) => (i === index ? { ...marker, time: newTime } : marker))
            .sort((a, b) => a.time - b.time);

        setMarkers(newMarkers);
        updateStateObjectData(multimediaDetail, setMultimediaDetail, "timeLineV2", newMarkers);
    };

    const markerTrackPercent = (markerTime) => {
        const d = Number(duration);
        const t = Number(markerTime);
        if (!d || !Number.isFinite(d) || d <= 0 || !Number.isFinite(t)) return 0;
        return Math.min(100, Math.max(0, (t / d) * 100));
    };

    return (
        <>
            <div
                style={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    margin: "auto",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                {/* Sticky Video Player & Marker Track */}
                <div style={{ position: "sticky", top: 0, zIndex: 2, background: "#fff", flexShrink: 0, width: "100%" }}>
                    <div
                        style={{
                            flexGrow: 1,
                            position: "relative",
                            width: "min(720px, 90%)",
                            minHeight: 320,
                            alignContent: "center",
                            margin: "auto",
                        }}
                    >
                        {videoSrc ? (
                            isCloudflarePlayer ? (
                                <iframe
                                    key={iframeKey}
                                    ref={iframeRef}
                                    id="raphousie-cloudflare-player"
                                    title="cloudflare-stream"
                                    src={videoSrc}
                                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                                    allowFullScreen
                                    onLoad={() => {
                                        const iframe =
                                            iframeRef.current ||
                                            document.getElementById("raphousie-cloudflare-player");
                                        const StreamCtor =
                                            typeof window !== "undefined" ? window.Stream : null;
                                        if (!iframe || !StreamCtor) return;
                                        try {
                                            const player = StreamCtor(iframe);
                                            streamPlayerRef.current = player;
                                            playWithAudio(player);
                                            attachCloudflareTimeSync(player);
                                        } catch (_) {
                                            /* ignore */
                                        }
                                    }}
                                    style={{
                                        width: "100%",
                                        height: 360,
                                        border: 0,
                                        borderRadius: "12px",
                                        backgroundColor: "black",
                                        display: "block",
                                    }}
                                />
                            ) : (
                                <video
                                    ref={videoRef}
                                    src={videoSrc}
                                    controls
                                    autoPlay={true}
                                    muted={false}
                                    playsInline
                                    controlsList="nodownload noplaybackrate"
                                    style={{
                                        width: "100%",
                                        height: 360,
                                        borderRadius: "12px",
                                        backgroundColor: "black",
                                    }}
                                    onLoadedMetadata={(e) => {
                                        ensureUnmuted(e.currentTarget);
                                        handleLoadedMetadata();
                                    }}
                                    onPlay={(e) => playWithAudio(e.currentTarget)}
                                    onTimeUpdate={(e) =>
                                        syncSelectedMarkerFromTime(e.currentTarget.currentTime)
                                    }
                                />
                            )
                        ) : (
                            <div
                                style={{
                                    width: "100%",
                                    height: 200,
                                    borderRadius: 12,
                                    background: "#111",
                                    color: "#aaa",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                No video preview — use Save & Preview with a Vimeo or Cloudflare ID
                            </div>
                        )}
                    </div>

                    {/* Marker Track */}
                    <div
                        style={{
                            position: "relative",
                            height: "8px",
                            background: "#bebebeff",
                            marginTop: "4px",
                        }}
                    >
                        {markers.length > 0 &&
                            markers.map((marker, index) => (
                                <div
                                    key={index}
                                    title={`${marker?.entityName || marker?.text || `Marker ${index + 1}`} @ ${formatTime(marker?.time)}`}
                                    style={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: `${markerTrackPercent(marker?.time)}%`,
                                        transform: "translateX(-50%)",
                                        width: "4px",
                                        height: "8px",
                                        background: isMarkerSelected(marker, index) ? "#2e7d32" : "red",
                                        cursor: "pointer",
                                    }}
                                    onClick={(e) => handleMarkerClick(marker, e, index)}
                                />
                            ))}
                    </div>
                    {selectedMarker && (
                        <div style={{ textAlign: "center", fontSize: 12, color: "#555", marginTop: 4 }}>
                            Playing at {formatTime(selectedMarker.time)}
                            {` (${toSeekSeconds(selectedMarker.time, duration).toFixed(1)}s)`}
                            {selectedMarker.entityName || selectedMarker?.details?.entity?.displayName
                                ? ` — ${selectedMarker.entityName || selectedMarker?.details?.entity?.displayName}`
                                : ""}
                            {selectedMarkerIndex >= 0 ? ` · Marker ${selectedMarkerIndex + 1}` : ""}
                        </div>
                    )}
                </div>

                {/* Marker List */}
                <div
                    style={{
                        marginTop: "10px",
                        flex: 1,
                        minHeight: 0,
                        maxHeight: "calc(100vh - 400px)",
                        overflowY: "auto",
                        paddingLeft: 10,
                        paddingRight: 10,
                        paddingBottom: 0,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                            marginTop: "5px",
                        }}
                    >
                        {markers?.length > 0 && <h3>🎯 Markers (click to play)</h3>}
                        <button onClick={() => addMarker()}>Add Marker</button>
                    </div>

                    {markers?.length > 0 && (
                        <div style={{ display: "grid", gap: "10px" }}>
                            {markers.map((marker, index) => (
                                <div
                                    key={index}
                                    onClick={(e) => handleMarkerClick(marker, e, index)}
                                    style={{
                                        padding: "12px 16px",
                                        borderRadius: "10px",
                                        backgroundColor: isMarkerSelected(marker, index) ? "#e6ffe6" : "#f5f5f5",
                                        border: isMarkerSelected(marker, index)
                                            ? "2px solid #4caf50"
                                            : "1px solid #ddd",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        {editingIndex === index ? (
                                            <input
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                onBlur={() => saveEdit(index)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") saveEdit(index);
                                                }}
                                                autoFocus
                                                style={{
                                                    fontSize: "14px",
                                                    padding: "4px 6px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #ccc",
                                                    width: "90%",
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <div
                                                    style={{
                                                        fontWeight: "bold",
                                                        fontSize: "14px",
                                                        marginTop: "-8",
                                                        marginBottom: "4px",
                                                        textAlign: "start",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 8,
                                                    }}
                                                >
                                                    <span>
                                                        {index + 1} · {formatTime(marker.time)}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            selectMarker(marker, index);
                                                            handleMarkerClick(marker, undefined, index);
                                                        }}
                                                        style={{
                                                            padding: "2px 8px",
                                                            fontSize: 11,
                                                            borderRadius: 6,
                                                            border: "1px solid #1976d2",
                                                            background: "#e3f2fd",
                                                            color: "#0d47a1",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        ▶ Play here
                                                    </button>
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <label style={{ fontSize: "12px" }}>⏱</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max={Math.floor(duration)}
                                                        value={marker.time}
                                                        onChange={(e) => handleTimeChange(index, e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            width: "70px",
                                                            padding: "4px 6px",
                                                            borderRadius: "6px",
                                                            border: "1px solid #ccc",
                                                            fontSize: "12px",
                                                        }}
                                                    />
                                                    <span style={{ fontSize: "12px", color: "#555" }}>
                                                        ({formatTime(marker.time)})
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {(marker?.time || marker?.details) && (
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "12px",
                                                background: "#fff",
                                                border: "1px solid #ddd",
                                                borderRadius: "8px",
                                                padding: "8px",
                                            }}
                                        >
                                            <img
                                                src={
                                                    generatePublicUrlForFileInAWSS3Bucket(
                                                        marker?.details?.multimediaUrl
                                                    ) ||
                                                    generatePublicUrlForFileInAWSS3Bucket(marker?.displayImageUrl) ||
                                                    ""
                                                }
                                                alt="entity"
                                                style={{
                                                    width: "60px",
                                                    height: "60px",
                                                    borderRadius: "8px",
                                                    objectFit: "cover",
                                                }}
                                            />
                                            <div style={{ flex: 1, textAlign: "start" }}>
                                                <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                                                    {marker?.details?.entity?.displayName}
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#777" }}>
                                                    EntityId: {marker?.entityId || marker?.entityID}
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#777" }}>
                                                    Entity Name:{" "}
                                                    {marker?.entityName || marker?.details?.entity?.displayName}
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#777" }}>
                                                    Entity Type:{" "}
                                                    {marker?.entityCategory || marker?.details?.entity?.type}
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#777" }}>
                                                    MultimediaId: {marker?.multimediaId}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div
                                        style={{
                                            fontWeight: "bold",
                                            fontSize: "14px",
                                            marginBottom: "4px",
                                            textAlign: "start",
                                            alignItems: "end",
                                            marginLeft: "8px",
                                        }}
                                    >
                                        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }} />

                                        {marker?.entityID ? (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveMarkerIndex(index);
                                                    setIsOpenEntertainmentEntityDialog(true);
                                                }}
                                                style={{
                                                    marginTop: "6px",
                                                    padding: "4px 8px",
                                                    fontSize: "12px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #4caf50",
                                                    background: "#e8f5e9",
                                                    color: "#388e3c",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                Edit Entity
                                            </button>
                                        ) : (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveMarkerIndex(index);
                                                    setIsOpenEntertainmentEntityDialog(true);
                                                }}
                                                style={{
                                                    marginTop: "6px",
                                                    padding: "4px 8px",
                                                    fontSize: "12px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #4caf50",
                                                    background: "#e8f5e9",
                                                    color: "#388e3c",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                ➕ Add Entity
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <RapHousieEntertainmentEntityDialog
                open={isOpenEntertainmentEntityDialog}
                onClose={() => {
                    console.log("multimediaDetail : ", multimediaDetail);
                    setIsOpenEntertainmentEntityDialog(false);
                }}
                onSubmit={(selectedItems) => {
                    console.log("selectedItems : ", selectedItems);
                }}
                selectedItem={multimediaDetail?.timeLineV2?.[activeMarkerIndex] || {}}
                handleChange={handleChange}
                fullScreen
            />
        </>
    );
};

export default VideoPlayerWithMarker;
